import React, { useEffect, useMemo, useRef, useState } from "react";
import type { ManifestAsset } from "../types.js";

interface Props {
  assets: ManifestAsset[];
  onSelect: (asset: ManifestAsset) => void;
  adminMode?: boolean;
  queued?: Set<string>;
  onToggleQueued?: (id: string) => void;
  // Download-selection (main grid only; mutually exclusive with adminMode).
  selectionEnabled?: boolean;
  selectedIds?: Set<string>;
  onToggleSelect?: (id: string) => void;
  onRangeSelect?: (orderedIds: string[], id: string) => void;
  onMarqueeSelect?: (ids: string[], additive: boolean) => void;
}

const MARQUEE_THRESHOLD = 6; // px of movement before a drag counts as a marquee

export function AssetGrid({
  assets,
  onSelect,
  adminMode,
  queued,
  onToggleQueued,
  selectionEnabled,
  selectedIds,
  onToggleSelect,
  onRangeSelect,
  onMarqueeSelect,
}: Props) {
  const gridRef = useRef<HTMLElement>(null);
  const [marquee, setMarquee] = useState<{ l: number; t: number; w: number; h: number } | null>(null);
  // Teardown for an in-progress marquee drag, so we can clean up if the grid
  // unmounts mid-drag (e.g. rapid folder navigation) before mouseup fires.
  const dragCleanupRef = useRef<(() => void) | null>(null);
  useEffect(() => () => dragCleanupRef.current?.(), []);

  // Stable list of ids in display order, for shift-click range selection.
  const orderedIds = useMemo(() => assets.map((a) => a.id), [assets]);

  // Marquee drag works in both modes: download-selection on the main grid,
  // disposition-queueing in admin mode (App supplies the matching handler).
  const marqueeEnabled = Boolean(selectionEnabled || (adminMode && onMarqueeSelect));

  function handleMouseDown(e: React.MouseEvent) {
    if (!marqueeEnabled || e.button !== 0) return;
    // A drag can start anywhere except the checkbox controls — starting on a
    // card is fine: below the movement threshold it stays a normal click
    // (opens the card), past it it becomes a marquee and the click is squelched.
    const target = e.target as HTMLElement;
    if (target.closest("input, label, a")) return;
    e.preventDefault();

    const startX = e.clientX;
    const startY = e.clientY;
    const additive = e.shiftKey || e.ctrlKey || e.metaKey;
    let moved = false;
    document.body.style.userSelect = "none";

    const onMove = (ev: MouseEvent) => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      if (!moved && Math.hypot(dx, dy) < MARQUEE_THRESHOLD) return;
      moved = true;
      setMarquee({
        l: Math.min(startX, ev.clientX),
        t: Math.min(startY, ev.clientY),
        w: Math.abs(dx),
        h: Math.abs(dy),
      });
    };

    const teardown = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.body.style.userSelect = "";
      dragCleanupRef.current = null;
      setMarquee(null);
    };

    const onUp = (ev: MouseEvent) => {
      teardown();
      if (!moved) return;
      // The drag started on (or ends over) a card: the browser will fire a
      // click right after mouseup, which would open that card's modal. Swallow
      // exactly that one click.
      document.addEventListener(
        "click",
        (ce) => {
          ce.stopPropagation();
          ce.preventDefault();
        },
        { capture: true, once: true },
      );
      const box = {
        left: Math.min(startX, ev.clientX),
        top: Math.min(startY, ev.clientY),
        right: Math.max(startX, ev.clientX),
        bottom: Math.max(startY, ev.clientY),
      };
      const hits: string[] = [];
      gridRef.current?.querySelectorAll<HTMLElement>("[data-asset-id]").forEach((node) => {
        const r = node.getBoundingClientRect();
        const intersects = r.left < box.right && r.right > box.left && r.top < box.bottom && r.bottom > box.top;
        if (intersects) {
          const id = node.getAttribute("data-asset-id");
          if (id) hits.push(id);
        }
      });
      if (hits.length) onMarqueeSelect?.(hits, additive);
    };

    dragCleanupRef.current = teardown;
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  }

  if (assets.length === 0) {
    return <div className="grid-empty">No assets match the current filters.</div>;
  }

  return (
    <main className="grid" ref={gridRef} onMouseDown={marqueeEnabled ? handleMouseDown : undefined}>
      {assets.map((a) => (
        <AssetCard
          key={a.id}
          asset={a}
          onSelect={onSelect}
          adminMode={adminMode}
          isQueued={queued?.has(a.id) ?? false}
          onToggleQueued={onToggleQueued}
          selectable={selectionEnabled}
          isSelected={selectedIds?.has(a.id) ?? false}
          orderedIds={orderedIds}
          onToggleSelect={onToggleSelect}
          onRangeSelect={onRangeSelect}
        />
      ))}
      {marquee && (
        <div
          className="marquee"
          style={{ left: marquee.l, top: marquee.t, width: marquee.w, height: marquee.h }}
          aria-hidden="true"
        />
      )}
    </main>
  );
}

interface CardProps {
  asset: ManifestAsset;
  onSelect: (a: ManifestAsset) => void;
  adminMode?: boolean;
  isQueued?: boolean;
  onToggleQueued?: (id: string) => void;
  selectable?: boolean;
  isSelected?: boolean;
  orderedIds: string[];
  onToggleSelect?: (id: string) => void;
  onRangeSelect?: (orderedIds: string[], id: string) => void;
}

function AssetCard({
  asset,
  onSelect,
  adminMode,
  isQueued,
  onToggleQueued,
  selectable,
  isSelected,
  orderedIds,
  onToggleSelect,
  onRangeSelect,
}: CardProps) {
  const thumb = pickThumbnailUrl(asset);
  return (
    <div
      className={`card-wrap ${isQueued ? "card-queued" : ""} ${isSelected ? "card-selected" : ""}`}
      data-asset-id={asset.id}
    >
      <button className="card" onClick={() => onSelect(asset)}>
        {thumb ? (
          <img src={thumb} alt={asset.name} loading="lazy" />
        ) : (
          <div className="card-placeholder">{asset.scheme}</div>
        )}
        <div className="card-meta">
          <div className="card-name" title={asset.name}>
            {asset.name}
          </div>
          {asset.sku && <div className="card-sku">{asset.sku}</div>}
        </div>
      </button>
      {selectable && onToggleSelect && (
        <label
          className="card-select"
          title={isSelected ? "Deselect" : "Select for download (shift-click for a range)"}
          onClick={(e) => {
            e.stopPropagation();
            if (e.shiftKey) {
              e.preventDefault();
              onRangeSelect?.(orderedIds, asset.id);
            }
          }}
        >
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onToggleSelect(asset.id)}
            aria-label={`Select ${asset.name} for download`}
          />
          <span className="card-select-box" aria-hidden="true" />
        </label>
      )}
      {adminMode && onToggleQueued && (
        <label
          className="card-admin-checkbox"
          title={isQueued ? "Remove from disposition queue" : "Queue for disposition (shift-click for a range)"}
          onClick={(e) => {
            e.stopPropagation();
            if (e.shiftKey) {
              e.preventDefault();
              onRangeSelect?.(orderedIds, asset.id);
            }
          }}
        >
          <input
            type="checkbox"
            checked={isQueued}
            onChange={() => onToggleQueued(asset.id)}
            aria-label={`Queue ${asset.name} for removal`}
          />
          <span className="card-admin-checkbox-box" aria-hidden="true" />
        </label>
      )}
    </div>
  );
}

/**
 * Pick the smallest variant ≥ 300px, preferring WebP.
 * Images use their normal resize ladder; videos use the poster_400__* variants
 * generated by the video-posters pipeline (same shape — width 400, format webp/jpeg).
 * Documents have no rasterized thumb available, so we render the placeholder.
 */
function pickThumbnailUrl(asset: ManifestAsset): string | undefined {
  if (asset.scheme === "document") return undefined;
  const targets = [...asset.variants]
    .filter((v) => v.width > 0)
    .sort((a, b) => a.width - b.width);
  const webp = targets.find((v) => v.format === "webp" && v.width >= 300);
  if (webp) return webp.url;
  const jpeg = targets.find((v) => v.format === "jpeg" && v.width >= 300);
  if (jpeg) return jpeg.url;
  return asset.scheme === "image" ? asset.variants[0]?.url : undefined;
}
